import AccountDeletionRequest from "@/components/AccountDeletionRequest";
import DashboardImage from "@/components/DashboardImage";
import LifetimeGrants from "@/components/LifetimeGrants";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, ExternalLink, MapPin, Sprout } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { formatLocation } from "@/components/FarmCard";

const PLAN_LABEL = {
  free: "Free", pro: "Pro", founder_pro: "Founder Pro",
};
const PLAN_STYLE = {
  free: "bg-[#F2ECE0] text-[#6E6258] border-[#E2DACB]",
  pro: "bg-[#EAF0E6] text-[#3E5733] border-[#BED2B5]",
  founder_pro: "bg-[#557448] text-[#F8F5EA] border-[#557448]",
};

export default function Dashboard() {
  const { t, tv, lang } = useLang();
  const { user } = useAuth();
  const [farms, setFarms] = useState(null);
  const [farmFilter, setFarmFilter] = useState("all");
  const [loadError, setLoadError] = useState(false);
  const [editingOwnerId, setEditingOwnerId] = useState(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [ownerError, setOwnerError] = useState("");
  const [ownerMessage, setOwnerMessage] = useState("");

  const assignOwner = async (farmId) => {
    const email = ownerEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      setOwnerError(t("auth.ownerInvalidEmail"));
      return;
    }
    setOwnerBusy(true);
    setOwnerError("");
    setOwnerMessage("");
    try {
      await api.post(`/admin/farms/${farmId}/assign-owner`, { email });
      const response = await api.get("/my/farms");
      setFarms(response.data);
      setEditingOwnerId(null);
      setOwnerEmail("");
      setOwnerMessage(t("auth.ownerAssigned"));
    } catch (error) {
      setOwnerError(error.response?.status === 404 ? t("auth.ownerNotFound") : t("auth.ownerAssignError"));
    } finally {
      setOwnerBusy(false);
    }
  };
  const visibleFarms = farms?.filter((f) => farmFilter === "all" || (farmFilter === "assigned" ? Boolean(f.owner_id) : !f.owner_id));

  useEffect(() => {
    api.get("/my/farms").then((r) => setFarms(r.data)).catch(() => { setLoadError(true); setFarms([]); });
  }, []);

  return (
    <div className="grain-bg min-h-[calc(100vh-72px)]" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="font-serif text-4xl font-semibold text-[#2A2521]">{t("auth.dashboardTitle")}</h1>
          {user?.is_admin && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#5C4636] text-[#F8F5EA]">{t("auth.adminBadge")}</span>
          )}
        </div>
        <p className="font-sans text-[#6E6258] mt-1.5">{user?.email}</p>
        {user?.is_admin && <p className="text-xs mt-2 break-all">Account ID: {user.id}</p>}

        <Link className="inline-block mt-4 underline text-[#557448]" to="/payments/history">{lang === "it" ? "I tuoi pagamenti" : "Your payments"}</Link>
        {user?.is_admin && <LifetimeGrants />}
        <h2 className="font-serif text-2xl font-semibold text-[#2A2521] mt-10 mb-5">{lang === "it" ? (user?.is_admin ? "Tutti i soggetti" : "I tuoi soggetti") : (user?.is_admin ? "All subjects" : "Your subjects")}</h2>

        {user?.is_admin && farms !== null && !loadError && (
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="admin-farm-filters">
            {["all", "assigned", "unassigned"].map((filter) => (
              <button key={filter} type="button" onClick={() => setFarmFilter(filter)}
                aria-pressed={farmFilter === filter}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${farmFilter === filter ? "bg-[#557448] text-[#F8F5EA] border-[#557448]" : "bg-[#FFFDF8] text-[#4A443D] border-[#E2DACB] hover:bg-[#F3EEE0]"}`}>
                {t(`auth.farmFilter_${filter}`)} ({farms.filter((f) => filter === "all" || (filter === "assigned" ? Boolean(f.owner_id) : !f.owner_id)).length})
              </button>
            ))}
          </div>
        )}

        {user?.is_admin && ownerMessage && (
          <p role="status" className="mb-4 text-sm text-[#557448]">{ownerMessage}</p>
        )}

        {farms === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[1, 2].map((i) => <div key={i} className="h-40 rounded-md bg-[#EFE9DA] animate-pulse" />)}
          </div>
        ) : loadError ? (
          <div role="alert" className="bg-[#FFFDF8] border border-[#E2DACB] rounded-md p-8 text-center font-sans text-[#B3261E]">{t("auth.farmsLoadError")}</div>
        ) : visibleFarms.length === 0 ? (
          <div data-testid="dashboard-no-farms" className="bg-[#FFFDF8] border border-[#E2DACB] rounded-md p-8 text-center font-sans text-[#6E6258]">
            {user?.is_admin ? t("auth.noMatchingFarms") : t("auth.noFarms")}
            {!user?.is_admin && <div className="flex flex-wrap justify-center gap-4 mt-4"><Link to="/atlas" className="underline">{lang === "it" ? "Esplora i soggetti" : "Explore subjects"}</Link><Link to="/projects" className="underline">{lang === "it" ? "Scopri i progetti" : "Discover projects"}</Link></div>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {visibleFarms.map((f) => {
              const plan = f.subscription?.effective_plan || "free";
              return (
                <div key={f.id} data-testid={`dashboard-farm-${f.id}`} className="bg-[#FFFDF8] border border-[#E2DACB] rounded-md overflow-hidden flex flex-col">
                  <div className="relative h-32">
                    {(f.cover_image || f.image) ? <DashboardImage source={f.cover_image || f.image} alt="" className="w-full h-full object-cover" /> : <div className="h-full bg-[#EFE9DA]" />}
                    <span className={`absolute top-3 right-3 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${PLAN_STYLE[plan]}`}>
                      {t("auth.plan")}: {PLAN_LABEL[plan]}
                    </span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-3">{f.profile_image && <DashboardImage source={f.profile_image} alt="" className="w-14 h-14 rounded-xl border border-[#E2DACB] bg-white object-cover shrink-0" />}<h3 className="font-serif text-xl font-semibold text-[#2A2521]">{f.name}</h3></div>
                    <p className="flex items-center gap-1.5 text-sm text-[#6E6258] mt-1">
                      <MapPin size={14} className="text-[#C46A3A]" /> {formatLocation(f.location, tv)}
                    </p>
                    <p className="flex items-center gap-1.5 text-sm text-[#4A443D] mt-1">
                      <Sprout size={14} className="text-[#557448]" /> {tv(f.main_crop)}
                    </p>
                    {user?.is_admin && (
                      <div className="mt-4 border-t border-[#EDE7D7] pt-3">
                        <p className="text-xs text-[#6E6258] mb-2">
                          {t(f.owner_id ? "auth.ownerAssignedStatus" : "auth.ownerUnassignedStatus")}
                          {f.owner_id && <span className="block mt-1 break-all">owner_id: {f.owner_id} · subject_id: {f.id}</span>}
                        </p>
                        {editingOwnerId === f.id ? (
                          <form onSubmit={(e) => { e.preventDefault(); assignOwner(f.id); }} className="space-y-2">
                            <label htmlFor={`owner-email-${f.id}`} className="block text-sm text-[#4A443D]">
                              {t("auth.ownerEmailLabel")}
                            </label>
                            <input id={`owner-email-${f.id}`} type="email" required autoComplete="off"
                              value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                              disabled={ownerBusy} placeholder="email@example.com"
                              className="w-full rounded-md border border-[#E2DACB] bg-white px-3 py-2 text-sm" />
                            {ownerError && <p role="alert" className="text-sm text-[#B3261E]">{ownerError}</p>}
                            <div className="flex flex-wrap gap-2">
                              <button type="submit" disabled={ownerBusy}
                                className="rounded-full bg-[#557448] px-4 py-2 text-sm text-white disabled:opacity-50">
                                {t("auth.ownerSave")}
                              </button>
                              <button type="button" disabled={ownerBusy}
                                onClick={() => { setEditingOwnerId(null); setOwnerError(""); setOwnerEmail(""); }}
                                className="rounded-full border border-[#E2DACB] px-4 py-2 text-sm text-[#4A443D]">
                                {t("auth.ownerCancel")}
                              </button>
                            </div>
                          </form>
                        ) : (
                          <button type="button" data-testid={`assign-owner-${f.id}`}
                            onClick={() => { setEditingOwnerId(f.id); setOwnerEmail(""); setOwnerError(""); setOwnerMessage(""); }}
                            className="text-sm font-medium text-[#557448] underline underline-offset-2">
                            {t(f.owner_id ? "auth.ownerChange" : "auth.ownerAssign")}
                          </button>
                        )}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#EDE7D7]">
                      <Link to={`/dashboard/farms/${f.id}/edit`} data-testid={`edit-farm-${f.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] text-sm font-medium px-4 h-9 transition-colors">
                        <Pencil size={14} /> {t("auth.edit")}
                      </Link>
                      {f.owner_id === user?.id && <Link className="inline-flex items-center rounded-full border border-[#E2DACB] px-4 h-9 text-sm" to={`/dashboard/farms/${f.id}/billing`}>{lang === "it" ? "Piano e incassi" : "Plan and payments"}</Link>}
                      <Link to={`/farm/${f.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E2DACB] text-[#4A443D] text-sm font-medium px-4 h-9 hover:bg-[#F3EEE0] transition-colors">
                        <ExternalLink size={14} /> {t("auth.openPublic")}
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <AccountDeletionRequest user={user} lang={lang} />
      </div>
    </div>
  );
}


